import React from 'react';
import type {TaskAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {TaskAssignmentStateEnum, TaskTypeEnum} from 'src/api/ProactApiClient';
import {useGetTaskAssignmentsListQuery} from 'src/redux/queries/taskAssignment';
import {CheckboxList} from 'src/design/atoms/CheckboxList';
import {isPendingSync} from 'src/offline/pendingSync';
import {
  PENDING_SYNC_PILL,
  TasksDataListMeta,
  taskDocumentTitle,
  taskRowSubText,
} from 'src/app/tasks/taskHelpers';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {useAppSelector} from 'src/redux/hooks';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useDebounce from 'src/shared/hooks/useDebounce';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {mapEnumToListOption} from 'src/utilities/enumUtils';
import {ActionPillConfig} from '../actionHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
const maxPageRows = 10;

export const ListCurrentUserActionAssignments: React.FC = () => {
  const {session} = useAppSelector(state => state.session);
  const userId = session?.user?.id;

  useSetDocumentTitle(taskDocumentTitle);
  const navigate = useAppNavigate();
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: 'my-assigned-actions',
    pageSize: maxPageRows,
    defaultFilters: {
      assignmentStates: [
        String(TaskAssignmentStateEnum.Assigned),
        String(TaskAssignmentStateEnum.Viewed),
      ],
      assignedToMe: true,
    },
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const assignmentStatuses = mapEnumToListOption(TaskAssignmentStateEnum);

  const {data: listResponse, isLoading} = useGetTaskAssignmentsListQuery(
    {
      taskType: TaskTypeEnum.Actions,
      assignedToUserId: filters.assignedToMe ? userId : undefined,
      assignmentStates: filters.assignmentStates.map((x: string) => +x),
      start,
      length,
      search: debouncedSearchTerm,
    },
    {skip: userId === undefined}
  );

  const indexListData: TaskAssignmentSimpleResponse[] | undefined =
    listResponse?.data;
  const totalRecords = listResponse?.totalRecords ?? 0;

  const handleNavigateToTask = (
    id: string | number,
    obj: TaskAssignmentSimpleResponse
  ) => navigate(PathUtility.ActionViewUser(obj.taskId, 'content'));

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  const handleAssignmentStatusFilter = (statusValues: string[]) => {
    setFilters(prevState => ({
      ...prevState,
      ...{assignmentStates: statusValues},
    }));
  };

  // Create action is currently disabled. @TODO: Enable this once the feature is ready.
  const actions: React.ReactNode[] = []; /*[
    <CreateButton onClick={handleAddAction}>Add Action</CreateButton>,
  ]; */

  return (
    <>
      <DataHeader
        titleText={'My Assigned Actions'}
        titleSize="sm"
        description="Actions currently assigned to you. They may have been created by you or others."
        searchTerm={searchTerm}
        actions={actions}
        filters={[
          <CheckboxList
            label="Task Statuses"
            values={assignmentStatuses}
            selected={filters.assignmentStates}
            onChange={handleAssignmentStatusFilter}
          />,
        ]}
        onSearchTermChange={handleSearchTerm}
        isFilterActive={false}
      />
      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={TasksDataListMeta}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={handleNavigateToTask}
        onIsRowDisabled={isPendingSync}
        onPageChange={handlePageChange}
        onGetPillMapping={(item: TaskAssignmentSimpleResponse) =>
          isPendingSync(item)
            ? [PENDING_SYNC_PILL]
            : ActionPillConfig(
                item.taskDefinition.state,
                item.state,
                undefined,
                item.dueAt
              )
        }
        onRowTextRender={item => item.taskDefinition?.subject}
        onRowSubTextRender={taskRowSubText}
        hasSearchTerm={searchTerm.trim().length > 0}
        hasCreationLogic={false}
      />
    </>
  );
};
